import {useState, useMemo, useCallback, type RefObject} from 'react'
import {Accordion, AccordionSummary, AccordionDetails, Typography, Box, Button} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ImageIcon from '@mui/icons-material/Image'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'
import WallpaperIcon from '@mui/icons-material/Wallpaper'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {useTranslation} from 'react-i18next'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import ScreenImagePanel from './ScreenImagePanel'
import ExportResolutionPanel from './ExportResolutionPanel'
import ExportDialog from './ExportDialog'
import Text2DPanel from './Text2DPanel'
import Text3DPanel from './Text3DPanel'
import Sprite2DPanel from './Sprite2DPanel'
import Sprite3DPanel from './Sprite3DPanel'
import type {RenderExportFn} from '../../../common/components/ExportHandler'
import {useAppSelector} from '../../../common/store'
import {selectActiveScene} from '../../../common/components/store/scenesSlice'
import {setExportFramePreview} from '../../../common/components/exportFramePreviewStore'
import useEnsureSavedProject from '../../hooks/useEnsureSavedProject'

// ── SidebarMenu — right panel with screen image + export ──────────

interface SidebarMenuProps {
  renderExportRef: RefObject<RenderExportFn | null>
  onExportRendering?: (rendering: boolean) => void
  workerBusy?: boolean
}

const RightSidebarMenu = ({renderExportRef, onExportRendering, workerBusy}: SidebarMenuProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const exportFrameEnabled = useAppSelector((s) => selectActiveScene(s).exportFrameEnabled)
  const {ensureSaved, dialog: saveDialog, isSaving} = useEnsureSavedProject()

  const handleOpenExport = useCallback(async () => {
    // Persist the project before paying credits — keeps the rendered scene tied
    // to a real project row that the render-history "Open project" link points to.
    const ok = await ensureSaved()
    if (ok) setExportDialogOpen(true)
  }, [ensureSaved])

  const handleCloseExport = useCallback(() => {
    setExportDialogOpen(false)
  }, [])

  // Hover preview — shows the export frame while pointer is over the button.
  // No-op if the frame is already enabled.
  const handleExportButtonEnter = useCallback(() => {
    if (!exportFrameEnabled) setExportFramePreview(true)
  }, [exportFrameEnabled])

  const handleExportButtonLeave = useCallback(() => {
    setExportFramePreview(false)
  }, [])

  return (
    <>
      <Box sx={sx.root}>
        {/* Scrollable accordions */}
        <Box sx={sx.accordionArea}>
          {/* Screen image (default open) */}
          <Accordion disableGutters square defaultExpanded sx={sx.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
              <ImageIcon sx={sx.sectionIcon} />
              <Typography sx={sx.sectionTitle}>{t('leftSidebar.screenImage')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={sx.details}>
              <ScreenImagePanel />
            </AccordionDetails>
          </Accordion>

          {/* 2D Text */}
          <Accordion disableGutters square defaultExpanded sx={sx.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
              <TextFieldsIcon sx={sx.sectionIcon} />
              <Typography sx={sx.sectionTitle}>{t('text2d.title')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={sx.details}>
              <Text2DPanel />
            </AccordionDetails>
          </Accordion>

          {/* 3D Text */}
          <Accordion disableGutters square defaultExpanded sx={sx.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
              <ViewInArIcon sx={sx.sectionIcon} />
              <Typography sx={sx.sectionTitle}>{t('text3d.title')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={sx.details}>
              <Text3DPanel />
            </AccordionDetails>
          </Accordion>

          {/* 2D Sprites */}
          <Accordion disableGutters square defaultExpanded sx={sx.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
              <PhotoLibraryIcon sx={sx.sectionIcon} />
              <Typography sx={sx.sectionTitle}>{t('sprite2d.title')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={sx.details}>
              <Sprite2DPanel />
            </AccordionDetails>
          </Accordion>

          {/* 3D Sprites */}
          <Accordion disableGutters square defaultExpanded sx={sx.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
              <WallpaperIcon sx={sx.sectionIcon} />
              <Typography sx={sx.sectionTitle}>{t('sprite3d.title')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={sx.details}>
              <Sprite3DPanel />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Export resolution — always visible above the export button */}
        <Box sx={sx.exportResolutionSection}>
          <ExportResolutionPanel />
        </Box>

        {/* Export button pinned at bottom */}
        <Box sx={sx.exportSection}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<FileDownloadIcon />}
            onClick={handleOpenExport}
            onMouseEnter={handleExportButtonEnter}
            onMouseLeave={handleExportButtonLeave}
            disabled={workerBusy || isSaving}
            sx={{fontSize: 12, textTransform: 'none'}}
          >
            {t('exportDialog.title')}
          </Button>
        </Box>
      </Box>
      {saveDialog}
      <ExportDialog
        open={exportDialogOpen}
        onClose={handleCloseExport}
        renderExportRef={renderExportRef}
        onRenderingChange={onExportRendering}
      />
    </>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    width: tokens.sidebar.width,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: tokens.sidebar.background,
    borderLeft: `1px solid ${tokens.sidebar.border}`,
    flexShrink: 0,
  },
  accordionArea: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  accordion: {
    backgroundColor: 'transparent',
    color: tokens.text.primary,
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  summary: {
    borderBottom: `1px solid ${tokens.accordion.border}`,
    minHeight: 48,
    '&.Mui-expanded': {minHeight: 48},
    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
      alignItems: 'center',
      gap: '8px',
      '&.Mui-expanded': {margin: '12px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 20,
  },
  sectionIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: tokens.text.primary,
  },
  details: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.accordion.border}`,
  },
  exportResolutionSection: {
    borderTop: `1px solid ${tokens.accordion.border}`,
    padding: '12px 16px 4px',
    flexShrink: 0,
  },
  exportSection: {
    borderTop: `1px solid ${tokens.accordion.border}`,
    padding: '12px 16px',
    flexShrink: 0,
  },
})

export default RightSidebarMenu
